'use client';

import { Box, Button, Text } from '@chakra-ui/react';
import { useRouter, useSearchParams } from 'next/navigation';
import { useEffect, useState, type ReactNode } from 'react';
import { useAuth } from '@/app/auth-provider';
import { AppShellToolbarPortal } from '@/components/core/app-layout/AppShellToolbar';
import { PageLoader } from '@/components/shared/PageLoader';
import { useProject, type Project } from '@/contexts/ProjectContext';
import { resolveWorkspaceEntry, workspaceSetupHref } from '@/lib/onboarding/workspace-entry';
import { getHybridOnboardingTarget } from './hybrid-onboarding-access';
import { WorkspaceSetupWizard } from './WorkspaceSetupWizard';

/** Resolve the accessible project before mounting any project-scoped setup hooks or chat. */
export function WorkspaceSetupEntry({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const {
    projects,
    currentProject,
    manageableWorkspaces,
    canCreatePersonalProject,
    isReady,
    loading,
    switchProject,
    createOnboardingProjectShell,
    completeProjectSetup,
  } = useProject();
  const router = useRouter();
  const params = useSearchParams();
  const [createdProject, setCreatedProject] = useState<Project | null>(null);
  const explicitNew = params.get('new_project') === 'true' || Boolean(params.get('reset'));
  const entry = resolveWorkspaceEntry({
    projects,
    currentProjectId: currentProject?.id || null,
    query: params,
    createdProjectId: explicitNew ? createdProject?.id : null,
  });
  const project = projects.find(item => item.id === entry.projectId) || null;
  const selectionPending = entry.mode === 'select';
  const creationTarget = getHybridOnboardingTarget({
    hasProject: Boolean(currentProject),
    forceNewProject: true,
    canCreatePersonalProject,
    manageableWorkspaces,
    requestedWorkspaceId: params.get('workspace_id'),
  });

  useEffect(() => {
    if (isReady && !loading && selectionPending && project) {
      switchProject(project.id, { preserveRoute: true });
    }
  }, [isReady, loading, project, selectionPending, switchProject]);

  useEffect(() => {
    if (!explicitNew) setCreatedProject(null);
  }, [explicitNew]);

  if (
    !user ||
    !isReady ||
    selectionPending ||
    (loading && (entry.mode !== 'home' || !currentProject))
  )
    return <PageLoader variant="section" minH="100%" />;
  if (
    entry.mode === 'unavailable' ||
    (entry.mode === 'new' && params.get('workspace_id') && !creationTarget)
  ) {
    return (
      <Box p="6">
        <Text>This workspace is unavailable to your account.</Text>
        <Button mt="3" onClick={() => router.replace('/app/workspace')}>
          Back to workspace
        </Button>
      </Box>
    );
  }
  if (entry.mode === 'home') return children;
  if ((!project && !creationTarget) || project?.current_user_role === 'editor') {
    return (
      <Box p="6">
        <Text>A workspace owner or admin can finish this setup.</Text>
        {project ? (
          <Button mt="3" onClick={() => router.replace('/app/workspace?new_chat=true')}>
            Open workspace
          </Button>
        ) : null}
      </Box>
    );
  }

  return (
    <Box h="full" overflowY="auto">
      <AppShellToolbarPortal
        navigation={
          <Text fontSize="sm" fontWeight="600">
            Workspace setup
          </Text>
        }
      />
      <WorkspaceSetupWizard
        key={project?.id || `new:${creationTarget?.workspaceId || 'personal'}`}
        project={project}
        workspaceId={project?.workspace_id || creationTarget?.workspaceId}
        workspaceName={creationTarget?.workspaceName}
        activeModuleId={params.get('setup_module')}
        returnTo={project ? workspaceSetupHref(project.id) : undefined}
        onActiveModuleChange={moduleId => {
          if (project) router.replace(workspaceSetupHref(project.id, moduleId), { scroll: false });
        }}
        onCreate={async input => {
          if (!creationTarget) throw new Error('This workspace is unavailable to your account.');
          const created = await createOnboardingProjectShell({
            ...input,
            ...(creationTarget?.workspaceId ? { workspace_id: creationTarget.workspaceId } : {}),
          });
          setCreatedProject(created);
          router.replace(workspaceSetupHref(created.id), { scroll: false });
          return created;
        }}
        onOpen={async status => {
          if (!project) throw new Error('Create your workspace first.');
          await completeProjectSetup(project.id, status);
          router.replace('/app/workspace?new_chat=true', { scroll: false });
        }}
      />
    </Box>
  );
}
